import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  TextField,
  Button,
  FormControlLabel,
  Checkbox,
  Paper,
  Input,
  Divider,
  Select,
  MenuItem,
  InputLabel,
  FormControl,
  Grid,
  IconButton,
} from "@mui/material";
import SideBar from "../dashboard admin/components/SideBar";
import api from "../../../services/api/api";
import { useNavigate } from "react-router-dom";
import CloseIcon from "@mui/icons-material/Close"; // Ícone de "X"
import ReactQuill, { Quill } from "react-quill-new";
import "react-quill-new/dist/quill.snow.css";
import "./quillCustom.css";


const Font = Quill.import("formats/font");
Font.whitelist = ["arial", "times-new-roman", "courier", "georgia", "monospace", "comic-sans"]; 
Quill.register(Font, true);

const CreateNewsAdmin = () => {
  const [title, setTitle] = useState("");
  const [content, setContent] = useState(""); // Estado do conteúdo
  const [isPublished, setIsPublished] = useState(false);
  const [image, setImage] = useState(null); // Capa da imagem
  const [otherImages, setOtherImages] = useState([]); // Outras imagens
  const [category, setCategory] = useState(""); // Estado para a categoria
  const [categories, setCategories] = useState([]); // Lista de categorias
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const navigate = useNavigate();

  // Carregar as categorias
  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const response = await api.get("/api/getAllCategoriesNews");
        setCategories(response.data); // Definir as categorias no estado
      } catch (err) {
        setError("Erro ao carregar categorias.");
      }
    };

    fetchCategories();
  }, []);

  // Handle input changes
  const handleTitleChange = (e) => setTitle(e.target.value);

  // Atualização do estado do conteúdo no editor
  const handleContentChange = (value) => setContent(value);

  const handlePublishedChange = () => setIsPublished((prev) => !prev);
  const handleImageChange = (e) => setImage(e.target.files[0]); // Atualiza imagem da capa
  const handleOtherImagesChange = (e) => setOtherImages(e.target.files); // Atualiza outras imagens
  const handleCategoryChange = (e) => setCategory(e.target.value); // Atualiza a categoria

  // Função para remover a imagem
  const removeImage = () => setImage(null);

  // Função para remover outras imagens
  const removeOtherImage = (index) => {
    const newImages = [...otherImages];
    newImages.splice(index, 1);
    setOtherImages(newImages);
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    const formData = new FormData();
    formData.append("title", title);
    formData.append("content", content); // Conteúdo como string
    formData.append("category", category); // Adiciona a categoria
    formData.append("isPublished", isPublished);

    // Capa da imagem
    if (image) {
      formData.append("CapaImage", image); // Envia a capa da imagem
    }

    // Outras imagens
    if (otherImages.length > 0) {
      Array.from(otherImages).forEach((file) => {
        formData.append("OthersImages", file); // Envia as outras imagens
      });
    }

    try {
      const token = localStorage.getItem("token");
      await api.post("/api/news", formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "multipart/form-data",
        },
      });
      navigate("/admin/news");
    } catch (err) {
      setError("Erro ao criar a notícia.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ display: "flex", minHeight: "100vh", backgroundColor: "#f4f6f8" }}>
      {/* Sidebar */}
      <Box>
        <SideBar />
      </Box>

      {/* Main Content */}
      <Box sx={{ flex: 1, p: 4 }}>
        <Typography variant="h4" fontWeight="bold" mb={2}>
          Criar Nova Notícia
        </Typography>

        {/* Error Message */}
        {error && (
          <Typography variant="body1" color="error" sx={{ mb: 2 }}>
            {error}
          </Typography>
        )}

        {/* News Form */}
        <Paper sx={{ p: 4, borderRadius: 2, boxShadow: 3 }}>
          <form onSubmit={handleSubmit}>
            {/* Title */}
            <TextField
              label="Título"
              variant="outlined"
              fullWidth
              value={title}
              onChange={handleTitleChange}
              sx={{ mb: 3 }}
            />

            {/* Content */}
            <div style={{ marginBottom: "16px" }}>
              <ReactQuill
                value={content}
                onChange={handleContentChange}
                modules={{
                  toolbar: [
                    [{ header: "1" }, { header: "2" }],
                    [{ font: ["arial", "times-new-roman", "courier", "georgia", "monospace", "comic-sans"] }], // Adicionando fontes
                    [{ size: ["small", false, "large", "huge"] }],
                    [{ color: [] }, { background: [] }],
                    [{ align: [] }, { indent: "-1" }, { indent: "+1" }],
                    ["bold", "italic", "underline", "strike"],
                    [{ list: "ordered" }, { list: "bullet" }],
                    ["blockquote", "code-block"],
                    ["link"],
                    ["clean"],
                  ],
                }}
              />
            </div>

            {/* Category */}
            <FormControl fullWidth sx={{ mb: 3 }}>
              <InputLabel>Categoria</InputLabel>
              <Select
                value={category}
                onChange={handleCategoryChange}
                label="Categoria"
              >
                {categories.map((cat) => (
                  <MenuItem key={cat._id} value={cat._id}>
                    {cat.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {/* Published Checkbox */}
            <FormControlLabel
              control={
                <Checkbox
                  checked={isPublished}
                  onChange={handlePublishedChange}
                  name="isPublished"
                  color="primary"
                />
              }
              label="Publicar agora"
              sx={{ mb: 3 }}
            />

            {/* Capa da Imagem */}
            <Box sx={{ mb: 3 }}>
              <Typography variant="body1" color="textSecondary" sx={{ mb: 1 }}>
                Selecione uma imagem para a capa
              </Typography>
              <Input
                type="file"
                inputProps={{ accept: "image/*" }}
                onChange={handleImageChange}
              />
              {image && (
                <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", position: "relative", marginTop: 1 }}>
                  <img
                    src={URL.createObjectURL(image)}
                    alt="Pré-visualização da Capa"
                    style={{
                      maxWidth: "200px", // Reduzindo o tamanho da imagem da capa
                      maxHeight: "150px",
                      objectFit: "cover",
                      borderRadius: "8px",
                      marginBottom: "8px",
                    }}
                  />
                  <IconButton
                    sx={{
                      position: "absolute",
                      top: 0,
                      right: 0,
                      backgroundColor: "rgba(255, 255, 255, 0.7)",
                    }}
                    onClick={removeImage}
                  >
                    <CloseIcon />
                  </IconButton>
                  <Typography variant="body2" color="textSecondary">
                    Capa selecionada: {image.name}
                  </Typography>
                </Box>
              )}
            </Box>

            {/* Outras Imagens */}
            <Box sx={{ mb: 3 }}>
              <Typography variant="body1" color="textSecondary" sx={{ mb: 1 }}>
                Selecione outras imagens
              </Typography>
              <Input
                type="file"
                inputProps={{ accept: "image/*", multiple: true }}
                onChange={handleOtherImagesChange}
              />
              {otherImages.length > 0 && (
                <Grid container spacing={2} sx={{ mt: 2 }}>
                  {Array.from(otherImages).map((file, index) => (
                    <Grid item key={index} xs={6} sm={4} md={3}>
                      <Box sx={{ position: "relative" }}>
                        <img
                          src={URL.createObjectURL(file)}
                          alt={`Imagem ${index + 1}`}
                          style={{
                            width: "100%",
                            height: "auto",
                            objectFit: "cover",
                            borderRadius: "8px",
                            maxHeight: "100px", // Reduzindo o tamanho da imagem
                          }}
                        />
                        <IconButton
                          sx={{
                            position: "absolute",
                            top: 0,
                            right: 0,
                            backgroundColor: "rgba(255, 255, 255, 0.7)",
                          }}
                          onClick={() => removeOtherImage(index)}
                        >
                          <CloseIcon />
                        </IconButton>
                      </Box>
                      <Typography variant="body2" color="textSecondary" sx={{ textAlign: "center", marginTop: 1 }}>
                        {file.name}
                      </Typography>
                    </Grid>
                  ))}
                </Grid>
              )}
            </Box>

            {/* Action Buttons */}
            <Divider sx={{ my: 2 }} />
            <Box sx={{ display: "flex", justifyContent: "space-between" }}>
              <Button
                type="submit"
                variant="contained"
                sx={{
                  background: "linear-gradient(90deg, #1cb5e0, #000851)",
                  textTransform: "none",
                  width: "48%",
                }}
                disabled={loading}
              >
                {loading ? "Salvando..." : "Salvar Notícia"}
              </Button>
              <Button
                variant="outlined"
                sx={{ width: "48%" }}
                onClick={() => navigate("/admin/news")}
              >
                Cancelar
              </Button>
            </Box>
          </form>
        </Paper>
      </Box>
    </Box>
  );
};

export default CreateNewsAdmin;
